/**
 * 构建版本感知 —— 「前端部署后页面不自动刷新」的架构解法。
 *
 * ── 现状与缺口（2026-10-20 核查）─────────────────────────────────────────
 * · `config/config.ts` 的 `define` 原本只注入 `CI` / `NODE_ENV`，**没有任何版本锚点**；
 * · `src/global.tsx` 里那套 ant-design-pro 模板遗留的 SW 更新分支是**死代码**：
 *   `defaultSettings.pwa === true` 但 umi pwa 插件并未启用 ⇒ `sw.updated` 永不派发，
 *   用户永远等不到「有新版本」的提示，也不知道该刷新；
 * · 结果是「发布完了，页面还是旧的，得自己 F5」。
 *
 * ── 锚点怎么来（双侧都能独立算出的同一个值）────────────────────────────
 * `scripts/gen-version.mjs` 在 `npm run build` 收尾时把 `dist/version.json` 写进产物：
 *   { buildId, entryFile, commit, buildTime }
 * 运行期的页面**不需要**预先知道自己是哪个构建：它直接从 DOM 里读自己**当前正在跑的
 * 入口产物文件名**（`umi.<hash>.js` —— `hash: true` 让文件名随内容变），
 * 再和 `version.json` 里的 `entryFile` 比。
 *
 * 为什么主锚点选 `entryFile` 而不是 `buildId`：
 *   两侧的 `buildId` 要一致，就得让「构建时 config 求值」与「构建后脚本」算出同一个值，
 *   任何时间戳参与都会漂移 ⇒ 会**误报新版本**（每次检查都提示）。而入口产物文件名是
 *   内容派生、双侧天然一致、且**正是「旧 HTML 引用了不存在的旧 chunk」的那把钥匙**。
 *   `buildId` / `commit` / `buildTime` 只用于展示与台账，不参与判定。
 *
 * ── 发现新版本后做什么（分级 + 护栏）──────────────────────────────────
 *   ① 护栏全通过（页面可见、无编辑态页签、无弹窗、无上传中、PDA 除外…见 `./reloadGuard`）
 *      → 记一次账后自动 `location.reload()`；
 *   ② 任一护栏不通过 → **只提示**（右下角常驻通知 + 「立即刷新」按钮），
 *      并在此后每次页面重新可见时**重新评估**：用户关掉编辑页签后就可能自动刷新；
 *   ③ 用户点过「稍后」→ 本次会话对该版本不再自动刷新；
 *   ④ 同一版本一个会话最多自动刷新 2 次（`reloadGuard` 台账），刷新后仍不一致就只提示
 *      —— 绝不无限刷。
 *
 * ⚠️ 不做高频轮询：只在「页面重新可见」+ 10 分钟间隔时检查，失败静默降级。
 */

import { Button, Space, notification } from 'antd';
import React from 'react';
import { hardReload, hasAutoReloadQuota, requestAutoReload, AUTO_RELOAD_ENABLED } from './reloadGuard';

/** 版本清单文件名（`scripts/gen-version.mjs` 产出，nginx 侧必须 no-cache） */
const VERSION_FILE_NAME = 'version.json';
/** 低频轮询间隔：10 分钟 */
export const VERSION_CHECK_INTERVAL_MS = 10 * 60 * 1000;
/** 页面重新可见时至少间隔这么久才再查一次，避免来回切标签页时反复请求 */
const MIN_INTERVAL_BETWEEN_CHECKS_MS = 60 * 1000;
/** 启动后首次检查的延迟：避开首屏渲染与页面自身的首拉请求 */
const FIRST_CHECK_DELAY_MS = 10 * 1000;
const NOTICE_KEY = 'dingyan-app-update';

export interface AppVersionInfo {
  /** 构建标识（形如 `526b37b57` / `526b37b57-dirty`）；仅展示与台账，不参与判定 */
  buildId: string;
  /** 入口产物文件名（如 `umi.1a2b3c4d.js`）—— **版本比对的主锚点** */
  entryFile: string;
  commit?: string;
  buildTime?: string;
}

const safeSession = (): Storage | null => {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
};

const readSession = (key: string): string | null => {
  try {
    return safeSession()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const writeSession = (key: string, value: string): void => {
  try {
    safeSession()?.setItem(key, value);
  } catch {
    /* 存储不可用时退化为「每次都会检测/提示」，不影响功能 */
  }
};

const baselineDir = (): string => `${window.location.origin}/`;

const isDevRuntime = (): boolean => {
  try {
    return process.env.NODE_ENV !== 'production';
  } catch {
    // define 未生效（process 不存在）时按生产处理，判定逻辑本身有静默降级
    return false;
  }
};

/**
 * 读取构建期注入的 `process.env.BUILD_ID`（`config/config.ts` 的 `define`）。
 * 只在 `try` 里访问：万一 define 没生效、`process` 不存在，不能让启动链崩掉。
 */
export function readRunningBuildId(): string {
  try {
    const raw = process.env.BUILD_ID;
    if (typeof raw !== 'string' || !raw) return '';
    // 兼容「被序列化两次」的极端情况（`"abc"` → `abc`）
    return raw.replace(/^"+|"+$/g, '');
  } catch {
    return '';
  }
}

/** 从 DOM 里找出当前正在运行的入口产物文件名（比对主锚点） */
function readRunningEntryUrl(): URL | null {
  if (typeof document === 'undefined') return null;
  const scripts = document.querySelectorAll('script[src]');
  for (let i = 0; i < scripts.length; i += 1) {
    const src = (scripts[i] as HTMLScriptElement).src;
    if (!src) continue;
    try {
      const url = new URL(src, window.location.href);
      if (url.origin !== window.location.origin) continue;
      const name = url.pathname.split('/').pop() ?? '';
      // hash: true ⇒ 入口是 umi.<hash>.js；这是与 index.html 里写的同一个名字
      if (/^umi\.[\w-]+\.js$/.test(name)) return url;
    } catch {
      /* 忽略无法解析的 src */
    }
  }
  return null;
}

export function readRunningEntryFile(): string {
  return readRunningEntryUrl()?.pathname.split('/').pop() ?? '';
}

/** 本次页面运行的版本信息 */
export function readRunningVersion(): AppVersionInfo {
  return {
    buildId: readRunningBuildId(),
    entryFile: readRunningEntryFile(),
  };
}

/** `version.json` 的地址：跟着入口脚本所在目录走，天然适配任何 publicPath */
function resolveVersionUrl(): string {
  const entryUrl = readRunningEntryUrl();
  return entryUrl
    ? new URL(VERSION_FILE_NAME, entryUrl).toString()
    : `${baselineDir()}${VERSION_FILE_NAME}`;
}

function normalizeDeployedVersion(payload: unknown): AppVersionInfo | null {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  const entryFile = typeof record.entryFile === 'string' ? record.entryFile : '';
  const buildId = typeof record.buildId === 'string' ? record.buildId : '';
  if (!entryFile && !buildId) return null;
  return {
    buildId,
    entryFile,
    commit: typeof record.commit === 'string' ? record.commit : undefined,
    buildTime: typeof record.buildTime === 'string' ? record.buildTime : undefined,
  };
}

/**
 * 拉取服务端当前部署的版本。**任何失败都静默返回 null**：
 * 未部署 version.json（旧流水线）、离线、被中间层兜成 HTML（开发服务器对未知路径
 * 一律回 `200 text/html`）—— 这些都不该在控制台刷屏，也不该影响任何功能。
 */
export async function fetchDeployedVersion(): Promise<AppVersionInfo | null> {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return null;
  try {
    const base = resolveVersionUrl();
    // 附加 cache-buster：即使中间层忽略了 no-store，也不会拿到其它版本的结果
    const url = `${base}${base.includes('?') ? '&' : '?'}_=${Date.now()}`;
    const response = await fetch(url, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    // 显式校验类型：nginx 少一条 `try_files $uri =404` 时缺文件会被兜成 200 text/html
    const contentType = response.headers.get('content-type') || '';
    if (!/application\/json/i.test(contentType)) return null;
    return normalizeDeployedVersion(await response.json());
  } catch {
    return null;
  }
}

/**
 * 判定「服务端已发布新版本」。
 * 只比 `entryFile`：两侧都有值时不等即新版；拿不到就返回 false（宁可不提示，也不误报）。
 */
export function isNewVersion(running: AppVersionInfo, deployed: AppVersionInfo): boolean {
  if (!running.entryFile || !deployed.entryFile) return false;
  return deployed.entryFile !== running.entryFile;
}

const versionScope = (deployed: AppVersionInfo): string =>
  `app-update::${deployed.entryFile || deployed.buildId}`;

const shorten = (info: AppVersionInfo): string =>
  [info.buildId, info.commit, info.buildTime].filter(Boolean).join(' · ') || '未标注';

function showUpdateNotice(deployed: AppVersionInfo, running: AppVersionInfo): void {
  notification.open({
    key: NOTICE_KEY,
    message: '系统已发布新版本',
    description: `当前页面运行的是旧版本（${shorten(running)}），服务器已是 ${shorten(deployed)}。请先保存未提交的内容，再刷新到最新版本。`,
    duration: 0,
    btn: React.createElement(
      Space,
      null,
      React.createElement(
        Button,
        {
          type: 'primary',
          size: 'small',
          onClick: () => {
            notification.destroy(NOTICE_KEY);
            // 用户显式要求刷新：走 hardReload，顺带清掉可能残留的 SW / Cache Storage
            hardReload();
          },
        },
        '立即刷新',
      ),
      React.createElement(
        Button,
        {
          type: 'default',
          size: 'small',
          onClick: () => {
            // 用户的显式意图：本会话对这个版本不再自动刷新，只保留手动入口
            writeSession(`dingyan-app-update-postponed::${versionScope(deployed)}`, '1');
            notification.destroy(NOTICE_KEY);
          },
        },
        '稍后',
      ),
    ),
  });
}

/**
 * 查一次版本并按策略处置。返回本次判定结果，便于联调（`window.__dingyanAppUpdate.check()`）。
 */
export async function checkForAppUpdate(): Promise<
  | 'dev-skipped'
  | 'no-version-info'
  | 'anchor-missing'
  | 'up-to-date'
  | 'reloaded'
  | 'notified'
> {
  if (isDevRuntime()) return 'dev-skipped';
  const running = readRunningVersion();
  const deployed = await fetchDeployedVersion();
  if (!deployed) return 'no-version-info';
  // 服务端有版本清单、本页却认不出自己的入口产物名 ⇒ 比对无从谈起。
  // 这种情况**必须留一行日志**（每会话一次），否则功能会静默失效而无人察觉
  // （例如产物命名规则变了：预期形如 umi.<hash>.js，见 scripts/gen-version.mjs）。
  if (!running.entryFile && deployed.entryFile) {
    warnAnchorMissingOnce();
    return 'anchor-missing';
  }
  if (!isNewVersion(running, deployed)) return 'up-to-date';

  const scope = versionScope(deployed);
  if (readSession(`dingyan-app-update-postponed::${scope}`) === '1') return 'notified';

  // 已经为这个版本记过账：不再自动刷新，只保证提示出现过一次
  const quotaLeft = hasAutoReloadQuota(scope) && AUTO_RELOAD_ENABLED;
  if (quotaLeft) {
    const blockReason = requestAutoReload(scope);
    if (blockReason === null) return 'reloaded';
  }
  // 护栏不通过 / 额度用尽：一次性提示，把决定权交给用户
  if (readSession(`dingyan-app-update-notified::${scope}`) !== '1') {
    writeSession(`dingyan-app-update-notified::${scope}`, '1');
    showUpdateNotice(deployed, running);
  }
  return 'notified';
}

let installed = false;
let lastCheckAt = 0;
let anchorWarned = false;

/** 入口产物名解析失败只告警一次：这是「功能静默失效」的唯一线索，但不能刷屏 */
function warnAnchorMissingOnce(): void {
  if (anchorWarned) return;
  anchorWarned = true;
  console.warn(
    '[appVersion] 未能从 DOM 解析出入口产物名（预期形如 umi.<hash>.js），本次及后续版本比对跳过。' +
      '请确认 config/config.ts 的 hash: true 仍开启、且产物命名规则未被改动。',
  );
}

/**
 * 装配版本检测（幂等）。由 `src/app.tsx` 在应用启动时调用一次。
 * 触发时机只有两个：**页面重新可见** + **10 分钟间隔**。
 */
export function installAppVersionWatch(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const runCheck = async () => {
    lastCheckAt = Date.now();
    await checkForAppUpdate();
  };

  window.setTimeout(runCheck, FIRST_CHECK_DELAY_MS);

  window.setInterval(runCheck, VERSION_CHECK_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    // 刚查过就不再查：来回切标签页不该变成高频轮询
    if (Date.now() - lastCheckAt < MIN_INTERVAL_BETWEEN_CHECKS_MS) return;
    void runCheck();
  });

  // 联调/验收入口（夜间无浏览器，验收步骤依赖它手动触发一次检查）
  (window as unknown as { __dingyanAppUpdate?: unknown }).__dingyanAppUpdate = {
    check: checkForAppUpdate,
    running: readRunningVersion,
    deployed: fetchDeployedVersion,
    intervalMs: VERSION_CHECK_INTERVAL_MS,
  };
}
